import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  forwardRef,
  useImperativeHandle,
} from "react";
import ReactDOM from "react-dom";
import ComponentToPrint from "../ComponentToPrint";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import ReactToPrint from "react-to-print";
import {
  Row,
  Col,
  Button,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { isF } from "../../../helpers/Roles";
import moment from "moment";
import {
  buildPrintPageStyle,
  buildSilentPrintHtml,
  getStoredPrintPaperWidthMm,
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import html2canvas from "html2canvas";

const ROLE_ALIAS = "bill";
export const INVOICE_IMAGE_CONTENT_WIDTH = 450;
export const INVOICE_IMAGE_CAPTURE_WIDTH = 480;
export const INVOICE_IMAGE_CAPTURE_SCALE = 2;
export const INVOICE_IMAGE_FALLBACK_SCALE = 1;
export const INVOICE_IMAGE_CAPTURE_SCALES = [
  INVOICE_IMAGE_CAPTURE_SCALE,
  INVOICE_IMAGE_FALLBACK_SCALE,
];

const setCaptureWidth = (node, width) => {
  node.style.width = width;
  node.style.minWidth = width;
  node.style.maxWidth = width;
  node.style.height = "auto";
  node.style.maxHeight = "none";
  node.style.overflow = "visible";
  node.style.boxSizing = "border-box";
};

export const applyInvoiceImageCaptureLayout = (printableNode) => {
  if (!printableNode) return () => {};

  const nodes = [
    { node: printableNode, width: INVOICE_IMAGE_CAPTURE_WIDTH },
    ...Array.prototype.map.call(
      printableNode.querySelectorAll(".invoice-contents"),
      (node) => ({ node, width: INVOICE_IMAGE_CONTENT_WIDTH }),
    ),
  ];
  const snapshots = nodes.map(({ node }) => ({
    node,
    style: node.getAttribute("style"),
  }));

  nodes.forEach(({ node, width }) => {
    setCaptureWidth(node, `${width}px`);
  });

  return () => {
    snapshots.forEach(({ node, style }) => {
      if (style === null) {
        node.removeAttribute("style");
      } else {
        node.setAttribute("style", style);
      }
    });
  };
};

export const getInvoiceImageCaptureOptions = (
  scale = INVOICE_IMAGE_CAPTURE_SCALE,
) => ({
  useCORS: true,
  scale,
  backgroundColor: "#ffffff",
  logging: false,
});

export const dataUrlToInvoiceImageBlob = (dataUrl) => {
  const match = /^data:([^;,]+)?;base64,(.*)$/.exec(dataUrl || "");
  if (!match || !match[2]) {
    throw new Error("Không thể tạo ảnh hóa đơn.");
  }

  const bytes = Uint8Array.from(window.atob(match[2]), (char) =>
    char.charCodeAt(0),
  );
  return new Blob([bytes], { type: match[1] || "image/png" });
};

export const canvasToInvoiceImageBlob = (canvas) =>
  new Promise((resolve, reject) => {
    if (
      !canvas ||
      (typeof canvas.toBlob !== "function" &&
        typeof canvas.toDataURL !== "function")
    ) {
      reject(new Error("Trình duyệt không hỗ trợ tạo ảnh hóa đơn."));
      return;
    }

    const encodeFromDataUrl = () => {
      try {
        resolve(dataUrlToInvoiceImageBlob(canvas.toDataURL("image/png")));
      } catch (error) {
        reject(error);
      }
    };

    if (typeof canvas.toBlob !== "function") {
      encodeFromDataUrl();
      return;
    }

    try {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        encodeFromDataUrl();
      }, "image/png");
    } catch (error) {
      encodeFromDataUrl();
    }
  });

export const createInvoiceImageBlob = async (generateCanvas) => {
  let latestError;

  for (const scale of INVOICE_IMAGE_CAPTURE_SCALES) {
    let canvas;
    try {
      canvas = await generateCanvas(scale);
      return await canvasToInvoiceImageBlob(canvas);
    } catch (error) {
      latestError = error;
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
    }
  }

  throw latestError || new Error("Không thể tạo ảnh hóa đơn.");
};

export const getInvoiceImageClipboardError = (
  clipboardWindow = window,
  clipboardNavigator = navigator,
) => {
  if (!clipboardWindow.isSecureContext) {
    return "Chrome chỉ cho phép sao chép ảnh khi VMASS được mở bằng HTTPS (hoặc localhost).";
  }

  if (
    !clipboardWindow.ClipboardItem ||
    !clipboardNavigator.clipboard ||
    typeof clipboardNavigator.clipboard.write !== "function"
  ) {
    return "Chrome hiện không hỗ trợ sao chép ảnh trực tiếp. Hãy dùng Chrome bản mới và mở VMASS bằng HTTPS.";
  }

  return null;
};

export const copyInvoiceImageToClipboard = async (
  blob,
  clipboardWindow = window,
  clipboardNavigator = navigator,
) => {
  const supportError = getInvoiceImageClipboardError(
    clipboardWindow,
    clipboardNavigator,
  );
  if (supportError) throw new Error(supportError);

  const clipboardItem = new clipboardWindow.ClipboardItem({
    "image/png": blob,
  });
  await clipboardNavigator.clipboard.write([clipboardItem]);
};

export const getInvoiceImageClipboardErrorMessage = (error) => {
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
    return "Chrome đã chặn quyền sao chép ảnh. Hãy cho phép Clipboard cho VMASS rồi thử lại.";
  }

  return error?.message || "Không thể sao chép ảnh hóa đơn.";
};

const resolvePrintableNode = (target) => {
  if (!target) return null;

  if (target instanceof HTMLElement) {
    return target;
  }

  try {
    const node = ReactDOM.findDOMNode(target);
    return node instanceof HTMLElement ? node : null;
  } catch (error) {
    return null;
  }
};

const ComponentToPrintWrapper = forwardRef((props, ref) => {
  const [state, setState] = useState({
    items: [],
    companyName: localStorage.getItem("companyName"),
    companyAddress: localStorage.getItem("companyAddress"),
    companyLogo: localStorage.getItem("companyLogo"),
    companyWebsite: localStorage.getItem("companyWebsite"),
    billDate: "",
    timeStart: "",
    pin_print: "",
    isGenerating: false,
  });

  const [canShare, setCanShare] = useState(false);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [invoiceProfileVersion, setInvoiceProfileVersion] = useState(0);
  const componentRef = useRef();

  const hasInvoicePaymentInfo = () => {
    const namePay = localStorage.getItem("namePay");
    const numberPay = localStorage.getItem("numberPay");
    const bankPay = localStorage.getItem("bankPay");

    return Boolean(
      namePay &&
        namePay !== "null" &&
        numberPay &&
        numberPay !== "null" &&
        bankPay &&
        bankPay !== "null",
    );
  };

  const syncInvoiceProfileToLocalStorage = async () => {
    const tokenStr = localStorage.getItem("access_token");

    try {
      const res = await axios.get(`${END_POINT}/user`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      const user = res.data?.data || {};
      const storageMap = {
        fullname: user.fullname || user.username || "",
        companyName: user.companyName || "",
        companyAddress: user.companyAddress || "",
        companyLogo: user.companyLogo || "",
        companyWebsite: user.companyWebsite || "",
        hotline: user.hotline || "",
        namePay: user.namePay || "",
        numberPay: user.numberPay || "",
        bankPay: user.bankPay || "",
        qrPayImage: user.qrPayImage || "",
      };

      Object.keys(storageMap).forEach((key) => {
        localStorage.setItem(key, storageMap[key]);
      });
      setInvoiceProfileVersion((version) => version + 1);
      return true;
    } catch (error) {
      console.error("Error syncing invoice profile:", error);
      return false;
    }
  };

  const prepareInvoiceForPrint = async () => {
    if (
      componentRef.current &&
      typeof componentRef.current.prepareForPrint === "function"
    ) {
      await componentRef.current.prepareForPrint();
    }
  };

  const handlePrintInvoice = async () => {
    if (!isF(ROLE_ALIAS)) return;

    if (window.electronAPI) {
      if (componentRef.current) {
        await prepareInvoiceForPrint();

        const printableNode = resolvePrintableNode(componentRef.current);
        if (!printableNode) {
          alert("Không thể lấy dữ liệu hóa đơn để in.");
          return;
        }

        const htmlContent = buildSilentPrintHtml(printableNode.outerHTML, {
          paperWidthMm: getStoredPrintPaperWidthMm("customer"),
          documentType: "customer",
        });
        const printerName = localStorage.getItem("default_printer") || null;
        window.electronAPI.printSilent(htmlContent, printerName).then((res) => {
          if (!res.success) {
            console.error("Print failed:", res.error);
            alert("Lỗi in: " + res.error);
          }
        });
      }
      return;
    }

    window.print();
  };

  useEffect(() => {
    dataBillRender();
  }, [props.orders_id]);

  useEffect(() => {
    if (navigator.share && navigator.canShare) {
      try {
        const dummyFile = new File([""], "dummy.png", { type: "image/png" });
        if (navigator.canShare({ files: [dummyFile] })) {
          setCanShare(true);
        }
      } catch (e) {
        console.error("Error checking Web Share support:", e);
      }
    }
  }, []);

  const dataBillRender = async () => {
    if (!props.orders_id.length) return false;

    if (!hasInvoicePaymentInfo()) {
      syncInvoiceProfileToLocalStorage();
    }

    let tokenStr = localStorage.getItem("access_token");
    axios
      .post(
        `${END_POINT + "/bill/invoice_by_orders_id"}`,
        {
          orders: props.orders_id,
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      )
      .then((res) => {
        return res.data;
      })
      .then((data) => {
        if (data.data.length) {
          const pins = [...new Set(data.data.map((item) => item.pin_print))];

          let pin_print = pins.join(" + ");

          const dates = [...new Set(data.data.map((item) => item.date))];
          let billDate = dates
            .map((d) => moment(d).format("DD/MM/YYYY"))
            .join(" + ");

          setState({
            ...state,
            items: data.data,
            companyName: localStorage.getItem("companyName"),
            companyAddress: localStorage.getItem("companyAddress"),
            companyLogo: localStorage.getItem("companyLogo"),
            companyWebsite: localStorage.getItem("companyWebsite"),
            billDate,
            timeStart: data.data[0].bill.time,
            pin_print: pin_print,
            isGenerating: false,
          });
        }
      });
  };

  const generateInvoiceImage = async (
    scale = INVOICE_IMAGE_CAPTURE_SCALE,
  ) => {
    if (!componentRef.current) {
      throw new Error("Không thể tìm thấy hóa đơn để tạo ảnh.");
    }

    await prepareInvoiceForPrint();

    const printableNode = resolvePrintableNode(componentRef.current);
    if (!printableNode) {
      throw new Error("Không thể lấy dữ liệu hóa đơn.");
    }

    const restoreCaptureLayout = applyInvoiceImageCaptureLayout(printableNode);
    try {
      return await html2canvas(
        printableNode,
        getInvoiceImageCaptureOptions(scale),
      );
    } finally {
      restoreCaptureLayout();
    }
  };

  const generateInvoiceImageBlob = () =>
    createInvoiceImageBlob(generateInvoiceImage);

  const handleDownloadImageFromBlob = (blob) => {
    const imageUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `hoa_don_${state.pin_print || "vmass"}.png`;
    link.href = imageUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(imageUrl), 0);
  };

  const handleCopyImage = async () => {
    if (state.isGenerating) return;

    const supportError = getInvoiceImageClipboardError();
    if (supportError) {
      NotificatioErrController(supportError);
      return;
    }

    setState((prev) => ({ ...prev, isGenerating: true }));

    try {
      const blob = await generateInvoiceImageBlob();
      await copyInvoiceImageToClipboard(blob);
      NotificatioSucController(
        "Đã sao chép ảnh hóa đơn. Đặt con trỏ vào Messenger/Zalo rồi nhấn Ctrl+V để dán ảnh."
      );
    } catch (error) {
      console.error("Lỗi sao chép ảnh hóa đơn:", error);
      NotificatioErrController(
        getInvoiceImageClipboardErrorMessage(error),
      );
    } finally {
      setState((prev) => ({ ...prev, isGenerating: false }));
    }
  };

  const handleShareImage = async () => {
    if (state.isGenerating) return;
    setState((prev) => ({ ...prev, isGenerating: true }));

    try {
      const blob = await generateInvoiceImageBlob();
      const file = new File(
        [blob],
        `hoa_don_${state.pin_print || "vmass"}.png`,
        { type: "image/png" }
      );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: "Hóa đơn VMASS",
            text: `Hóa đơn thanh toán #${state.pin_print}`,
          });
        } catch (shareErr) {
          if (shareErr.name !== "AbortError") {
            console.error("Web Share failed:", shareErr);
            NotificatioErrController("Lỗi khi chia sẻ ảnh.");
          }
        }
      } else {
        NotificatioErrController(
          "Trình duyệt hoặc thiết bị không hỗ trợ tính năng chia sẻ trực tiếp."
        );
      }
    } catch (error) {
      console.error("Lỗi chia sẻ ảnh hóa đơn:", error);
      NotificatioErrController(error.message || "Lỗi chia sẻ ảnh hóa đơn.");
    } finally {
      setState((prev) => ({ ...prev, isGenerating: false }));
    }
  };

  const handleDownloadImage = async () => {
    if (state.isGenerating) return;
    setState((prev) => ({ ...prev, isGenerating: true }));

    try {
      const blob = await generateInvoiceImageBlob();
      handleDownloadImageFromBlob(blob);
      NotificatioSucController("Đã tải ảnh hóa đơn về thiết bị.");
    } catch (error) {
      console.error("Lỗi tải ảnh hóa đơn:", error);
      NotificatioErrController(error.message || "Lỗi tải ảnh hóa đơn.");
    } finally {
      setState((prev) => ({ ...prev, isGenerating: false }));
    }
  };

  useImperativeHandle(ref, () => ({
    printInvoice: handlePrintInvoice,
    copyImage: handleCopyImage,
    shareImage: handleShareImage,
    downloadImage: handleDownloadImage,
  }));

  return (
    <Fragment>
      <div className="invoice__container position-sticky-custom">
        <Row className="invoice-react">
        <Col className="mb-5 invoice-print-btn">
          <div className="invoice-react">
            <div className="mb-5 invoice-contents bg-transparent">
              <div className="align-self-center order-invoice">
                <div className="mt-3 mb-4 d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                  {isF(ROLE_ALIAS) ? (
                    window.electronAPI ? (
                        <Button
                          color="secondary"
                          outline
                        onClick={handlePrintInvoice}
                      >
                        <i className="simple-icon-printer mr-1" /> In hóa đơn
                      </Button>
                    ) : (
                      componentRef && (
                        <ReactToPrint
                          trigger={() => (
                            <Button color="secondary" outline>
                              <i className="simple-icon-printer mr-1" /> In hóa đơn
                            </Button>
                          )}
                          content={() => componentRef.current}
                          pageStyle={buildPrintPageStyle(
                            getStoredPrintPaperWidthMm("customer"),
                          )}
                          onBeforeGetContent={prepareInvoiceForPrint}
                        />
                      )
                    )
                  ) : (
                    <Button color="secondary" outline className="disable">
                      <i className="simple-icon-printer mr-1" /> In hóa đơn
                    </Button>
                  )}

                  <Button
                    color="primary"
                    outline
                    onClick={handleCopyImage}
                    disabled={state.isGenerating || !state.items.length}
                  >
                    {state.isGenerating ? (
                      <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                    ) : (
                      <i className="simple-icon-docs mr-1" />
                    )}
                    Sao chép ảnh
                  </Button>

                  <ButtonDropdown
                    isOpen={moreActionsOpen}
                    toggle={() => setMoreActionsOpen(!moreActionsOpen)}
                  >
                    <DropdownToggle color="secondary" outline caret>
                      Khác
                    </DropdownToggle>
                    <DropdownMenu right>
                      {canShare && (
                        <DropdownItem
                          onClick={handleShareImage}
                          disabled={state.isGenerating || !state.items.length}
                        >
                          <i className="simple-icon-share mr-2" />
                          Chia sẻ (Zalo/Messenger)
                        </DropdownItem>
                      )}
                      <DropdownItem
                        onClick={handleDownloadImage}
                        disabled={state.isGenerating || !state.items.length}
                      >
                        <i className="simple-icon-cloud-download mr-2" />
                        Tải ảnh
                      </DropdownItem>
                    </DropdownMenu>
                  </ButtonDropdown>
                </div>
                <ComponentToPrint
                  key={`invoice-preview-${invoiceProfileVersion}`}
                  items={state.items}
                  companyName={state.companyName}
                  companyAddress={state.companyAddress}
                  companyLogo={state.companyLogo}
                  companyWebsite={state.companyWebsite}
                  billDate={state.billDate}
                  timeStart={state.timeStart}
                  pin_print={state.pin_print}
                  ref={componentRef}
                  discount={null}
                  tax_fee={null}
                  fee_other={null}
                  cardWrapper={true}
                  customer_name={state.items[0]?.customer_name}
                  customer_phone={state.items[0]?.phone}
                  customer_address={state.items[0]?.customer_address}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
      </div>

    </Fragment>
  );
});

export default ComponentToPrintWrapper;
